import { createSelector, Selector } from 'reselect'
import {
  AIObjectForProjectResponse,
  ContentForProjectResponse,
  DeploymentStatus,
  FormControlForProjectResponse,
  FormForProjectResponse,
  IterationAIObjectFilterResponse,
  IterationContentFilterResponse,
  IterationFormControlFilterResponse,
  IterationFormFilterResponse,
  IterationMetricResponse,
  IterationResponse,
  IterationStatus,
  IterationUserFilterResponse,
  PageForIterationResponse,
} from '@tabeeb/contracts/microservices/swagger-ai-service'
import { RootState } from '@tabeeb/store'
import { FormAnswerStatus, UserSearchInfoResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { getTrainingSettings } from './trainingSettings'
import { AnnotationDates, ExtendedIterationResponse } from '../types'
import { AnnotationSource } from '../enums'

export * from './trainingSettings'
export * from './annotationSourceOptions'

export const getAnnotationSource = (state: RootState) => state.modelTraining.trainingSettings.annotationSource
export const getCurrentIterationPagesDisplayMode = (state: RootState) =>
  state.modelTraining.currentIterationPagesDisplayMode
export const getCustomMetricsCharts = (state: RootState) => state.modelTraining.customMetricsCharts
export const getIsDeploymentStarting = (state: RootState) => state.modelTraining.isDeploymentStarting
export const getIsFastExcludeModeOn = (state: RootState) => state.modelTraining.isFastExcludeModeOn
export const getIsIterationComponentsLoading = (state: RootState) => state.modelTraining.isIterationComponentsLoading
export const getIsIterationDeploymentStatusLoading = (state: RootState) =>
  state.modelTraining.isIterationDeploymentStatusLoading
export const getIsIterationStatusLoading = (state: RootState) => state.modelTraining.isIterationStatusLoading
export const getIsMetricsLoading = (state: RootState) => state.modelTraining.isMetricsLoading
export const getIsPagesAnalysing = (state: RootState) => state.modelTraining.isPagesAnalysing
export const getIsPagesAnalysisDialogOpen = (state: RootState) => state.modelTraining.isPagesAnalysisDialogOpen
export const getIsPagesLoading = (state: RootState) => state.modelTraining.isPagesLoading
export const getIsProjectsLoading = (state: RootState) => state.modelTraining.isProjectsLoading
export const getIsTrainingSettingsDialogOpen = (state: RootState) => state.modelTraining.isTrainingSettingsDialogOpen
export const getIsTrainingStarting = (state: RootState) => state.modelTraining.isTrainingStarting
export const getPageNumber = (state: RootState) => state.modelTraining.pageNumber
export const getPages = (state: RootState): PageForIterationResponse[] => state.modelTraining.pages.Pages
export const getPagesTotalCount = (state: RootState): number => state.modelTraining.pages.TotalCount
export const getPageSize = (state: RootState) => state.modelTraining.pageSize
export const getProjectForEdit = (state: RootState) => state.modelTraining.projectForEdit
export const getProjects = (state: RootState) => state.modelTraining.projects.Projects
export const getProjectsTotalCount = (state: RootState) => state.modelTraining.projects.TotalCount
export const getProjectToDelete = (state: RootState) => state.modelTraining.projectToDelete

export const getSelectedAIObjects = (state: RootState): AIObjectForProjectResponse[] =>
  state.modelTraining.selectedAIObjects

export const getSelectedAnnotationDates = (state: RootState): AnnotationDates =>
  state.modelTraining.selectedAnnotationDates

export const getSelectedAnnotationOwners = (state: RootState): UserSearchInfoResponse[] =>
  state.modelTraining.selectedAnnotationOwners

export const getSelectedAnswerers = (state: RootState): UserSearchInfoResponse[] =>
  state.modelTraining.selectedAnswerers

export const getSelectedApprovers = (state: RootState): UserSearchInfoResponse[] =>
  state.modelTraining.selectedApprovers

export const getSelectedContents = (state: RootState): ContentForProjectResponse[] =>
  state.modelTraining.selectedContents

export const getSelectedFormAnswerStatuses = (state: RootState): FormAnswerStatus[] =>
  state.modelTraining.selectedFormAnswerStatuses

export const getSelectedFormAnswerValue = (state: RootState): string | null =>
  state.modelTraining.selectedFormAnswerValue

export const getSelectedFormControls = (state: RootState): FormControlForProjectResponse[] =>
  state.modelTraining.selectedFormControls

export const getSelectedForms = (state: RootState): FormForProjectResponse[] => state.modelTraining.selectedForms
export const getSelectedIterationId = (state: RootState) => state.modelTraining.selectedIterationId
export const getSelectedPagesForExclusion = (state: RootState) => state.modelTraining.selectedPagesForExclusion
export const getSelectedProjectIterations = (state: RootState) => state.modelTraining.iterations
export const getSelectedReviewers = (state: RootState): UserSearchInfoResponse[] =>
  state.modelTraining.selectedReviewers
export const getSelectedSearchField = (state: RootState) => state.modelTraining.selectedSearchField

export const getIsAnyIterationSelected = createSelector([getSelectedIterationId], (selectedIterationId) => {
  return selectedIterationId !== 0
})

export const getSelectedIteration: Selector<RootState, ExtendedIterationResponse | undefined> = createSelector(
  [getSelectedIterationId, getSelectedProjectIterations],
  (selectedIterationId, iterations) => {
    const selectedIteration = iterations.find((iteration) => iteration.Id === selectedIterationId)
    return selectedIteration
  }
)

const makeSelectorBasedOnSelectedIteration = <TFilterResult, TFinalResult>(
  filterSelector: Selector<RootState, TFilterResult>,
  callback: (iteration: ExtendedIterationResponse) => TFinalResult
): Selector<RootState, TFilterResult | TFinalResult> =>
  createSelector([filterSelector, getSelectedIteration], (items, selectedIteration) => {
    if (!selectedIteration) {
      return items
    }
    return callback(selectedIteration)
  })

export const getContentsBasedOnSelectedIteration: Selector<
  RootState,
  ContentForProjectResponse[] | IterationContentFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<ContentForProjectResponse[], IterationContentFilterResponse[]>(
  getSelectedContents,
  (iteration) => iteration.Contents
)

export const getPagesBasedOnSelectedIteration: Selector<RootState, PageForIterationResponse[]> =
  makeSelectorBasedOnSelectedIteration<PageForIterationResponse[], PageForIterationResponse[]>(
    getPages,
    (iteration) => iteration.Pages
  )

export const getPagesTotalCountBasedOnSelectedIteration: Selector<RootState, number> =
  makeSelectorBasedOnSelectedIteration<number, number>(getPagesTotalCount, (iteration) => iteration.PagesTotalCount)

export const getFormsBasedOnSelectedIteration: Selector<
  RootState,
  FormForProjectResponse[] | IterationFormFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<FormForProjectResponse[], IterationFormFilterResponse[]>(
  getSelectedForms,
  (iteration) => iteration.Forms
)

export const getFormAnswerValueBasedOnSelectedIteration: Selector<RootState, string | null | undefined> =
  makeSelectorBasedOnSelectedIteration<string | null, string | null | undefined>(
    getSelectedFormAnswerValue,
    (iteration) => iteration.FormAnswerValue
  )

export const getFormControlsBasedOnSelectedIteration: Selector<
  RootState,
  FormControlForProjectResponse[] | IterationFormControlFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<FormControlForProjectResponse[], IterationFormControlFilterResponse[]>(
  getSelectedFormControls,
  (iteration) => iteration.FormControls
)

export const getFormAnswerStatusesBasedOnSelectedIteration: Selector<RootState, FormAnswerStatus[]> =
  makeSelectorBasedOnSelectedIteration<FormAnswerStatus[], FormAnswerStatus[]>(
    getSelectedFormAnswerStatuses,
    (iteration) => iteration.FormAnswerStatuses
  )

export const getAnswerersBasedOnSelectedIteration: Selector<
  RootState,
  UserSearchInfoResponse[] | IterationUserFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<UserSearchInfoResponse[], IterationUserFilterResponse[]>(
  getSelectedAnswerers,
  (iteration) => iteration.Answerers
)

export const getReviewersBasedOnSelectedIteration: Selector<
  RootState,
  UserSearchInfoResponse[] | IterationUserFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<UserSearchInfoResponse[], IterationUserFilterResponse[]>(
  getSelectedReviewers,
  (iteration) => iteration.Reviewers
)

export const getApproversBasedOnSelectedIteration: Selector<
  RootState,
  UserSearchInfoResponse[] | IterationUserFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<UserSearchInfoResponse[], IterationUserFilterResponse[]>(
  getSelectedApprovers,
  (iteration) => iteration.Approvers
)

export const getAiObjectsBasedOnSelectedIteration: Selector<
  RootState,
  AIObjectForProjectResponse[] | IterationAIObjectFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<AIObjectForProjectResponse[], IterationAIObjectFilterResponse[]>(
  getSelectedAIObjects,
  (iteration) => iteration.AIObjects
)

export const getAnnotationOwnersBasedOnSelectedIteration: Selector<
  RootState,
  UserSearchInfoResponse[] | IterationUserFilterResponse[]
> = makeSelectorBasedOnSelectedIteration<UserSearchInfoResponse[], IterationUserFilterResponse[]>(
  getSelectedAnnotationOwners,
  (iteration) => iteration.AnnotationOwners
)

export const getAnnotationDatesBasedOnSelectedIteration: Selector<
  RootState,
  | AnnotationDates
  | {
      StartDate: IterationResponse['AnnotationStartDate']
      EndDate: IterationResponse['AnnotationEndDate']
    }
> = makeSelectorBasedOnSelectedIteration<
  AnnotationDates,
  {
    StartDate: IterationResponse['AnnotationStartDate']
    EndDate: IterationResponse['AnnotationEndDate']
  }
>(getSelectedAnnotationDates, (iteration) => ({
  StartDate: iteration.AnnotationStartDate,
  EndDate: iteration.AnnotationEndDate,
}))

export const getMetricsBasedOnSelectedIteration: Selector<RootState, IterationMetricResponse[]> = createSelector(
  [getSelectedIteration],
  (selectedIteration) => {
    if (!selectedIteration) {
      return []
    }

    return selectedIteration.Metrics
  }
)

export const getCustomMetricsChartsBasedOnSelectedIteration = createSelector(
  [getSelectedIterationId, getCustomMetricsCharts],
  (iterationId, charts) => {
    return charts.filter((chart) => chart.iterationId === iterationId)
  }
)

export const getIsTrainingEnabled: Selector<RootState, boolean> = createSelector(
  [getIsTrainingStarting, getTrainingSettings, getPages, getIsAnyIterationSelected],
  (isTrainingStarting, trainingSettings, pages, isAnyIterationSelected) => {
    return !isAnyIterationSelected && !isTrainingStarting && pages.length > 0 && trainingSettings.annotationSource >= 0
  }
)

export const getIsAnalysingEnabled: Selector<RootState, boolean> = createSelector(
  [getIsTrainingStarting, getIsAnyIterationSelected],
  (isTrainingStarting, isAnyIterationSelected) => {
    return !isAnyIterationSelected && !isTrainingStarting
  }
)

export const getIsIterationDeploymentEnabled: Selector<RootState, boolean> = createSelector(
  [getIsDeploymentStarting, getSelectedIteration],
  (isDeploymentStarting, selectedIteration) => {
    if (!selectedIteration) {
      return false
    }

    return (
      !isDeploymentStarting &&
      selectedIteration.Status === IterationStatus.Completed &&
      (selectedIteration.DeploymentStatus === DeploymentStatus.Stopped ||
        selectedIteration.DeploymentStatus === DeploymentStatus.NotDeployed ||
        selectedIteration.DeploymentStatus === DeploymentStatus.Failed ||
        selectedIteration.DeploymentStatus === DeploymentStatus.FailedToSendToDeploy)
    )
  }
)

export const getIsProjectComponentsLoading: Selector<RootState, boolean> = createSelector(
  [getIsTrainingStarting, getIsIterationComponentsLoading],
  (isTrainingStarting, isIterationComponentsLoading) => {
    return isTrainingStarting || isIterationComponentsLoading
  }
)

export const getIsAnySearchEntitySelected: Selector<RootState, boolean> = createSelector(
  [getSelectedAIObjects, getSelectedContents, getSelectedForms],
  (selectedAIObjects, selectedContents, selectedForms) => {
    return selectedAIObjects.length > 0 || selectedContents.length > 0 || selectedForms.length > 0
  }
)

export const getIsAnnotationSourceSelected: Selector<RootState, boolean> = createSelector(
  [getAnnotationSource],
  (annotationSource) => {
    return annotationSource !== AnnotationSource.Unknown
  }
)

export const getIsSelectedIterationCompleted: Selector<RootState, boolean> = createSelector(
  [getSelectedIteration],
  (selectedIteration) => {
    if (!selectedIteration) {
      return false
    }

    return selectedIteration.Status === IterationStatus.Completed
  }
)
